import { useEffect, useState } from 'react';
import { JsonRpcProvider } from '@ethersproject/providers';
import {
    getTokenContract,
    getTokenV2Contract,
    getAuctionContract,
    getAuctionV2Contract,
    getMarketSettingsContract,
    getMarketSettingsV2Contract,
    getRoyaltyEngineContract,
} from 'dibs-crypto-utils/exports/getContract';
import { getChainIdFromEnv } from 'dibs-crypto-utils/exports/supportedChains';
import type {
    FirstDibsAuction,
    FirstDibsToken,
    FirstDibsTokenV2,
    FirstDibsMarketSettings,
    FirstDibsMarketSettingsV2,
    FirstDibsAuctionV2,
    RoyaltyEngineV1,
} from '1stdibs-contracts/typechain';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';

type Contracts = {
    auction: FirstDibsAuction;
    token: FirstDibsToken;
    tokenV2: FirstDibsTokenV2;
    marketSettings: FirstDibsMarketSettings;
    marketSettingsV2: FirstDibsMarketSettingsV2 | null;
    auctionV2: FirstDibsAuctionV2 | null;
    royaltyEngine: RoyaltyEngineV1 | null;
};

export function useDibsContracts(
    library?: JsonRpcProvider
): [contracts: Contracts | null, error: boolean | null] {
    const [contracts, setContracts] = useState<Contracts | null>(null);
    const [error, setError] = useState(false);
    const { alchemyProvider } = useWeb3Connection();
    const chainId = getChainIdFromEnv();

    useEffect((): void => {
        (async function fetchContracts() {
            const provider = library || alchemyProvider;
            if (provider && chainId) {
                try {
                    const [
                        tokenContract,
                        tokenV2Contract,
                        auctionContract,
                        marketSettingsContract,
                        auctionV2Contract,
                        marketSettingsV2Contract,
                        royaltyEngineContract,
                    ] = await Promise.all([
                        getTokenContract(provider, chainId),
                        getTokenV2Contract(provider, chainId),
                        getAuctionContract(provider, chainId),
                        getMarketSettingsContract(provider, chainId),
                        getAuctionV2Contract(provider, chainId),
                        getMarketSettingsV2Contract(provider, chainId),
                        getRoyaltyEngineContract(provider, chainId),
                    ]);

                    if (
                        tokenContract &&
                        auctionContract &&
                        marketSettingsContract &&
                        tokenV2Contract
                    ) {
                        setContracts({
                            token: tokenContract,
                            tokenV2: tokenV2Contract,
                            auction: auctionContract,
                            marketSettings: marketSettingsContract,
                            marketSettingsV2: marketSettingsV2Contract,
                            auctionV2: auctionV2Contract,
                            royaltyEngine: royaltyEngineContract,
                        });
                    } else {
                        setError(true);
                    }
                } catch {
                    setError(true);
                }
            }
        })();
    }, [alchemyProvider, chainId, library]);

    return [contracts, error];
}
